import React, { useState } from 'react';
import api from '../../../utils/api';

const statusStyle = (s) => ({
  pending:          'bg-amber-100 text-amber-700',
  pending_payment:  'bg-orange-100 text-orange-700',
  confirmed:        'bg-blue-100 text-blue-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  completed:        'bg-emerald-100 text-emerald-700',
  cancelled:        'bg-red-100 text-red-600',
}[s] || 'bg-gray-100 text-gray-600');

function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-2">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

export default function OrdersPanel({ orders, loading, onUpdateStatus }) {
  const [expanded, setExpanded] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState({});  // { orderId: { name, phone } }
  const [assigning, setAssigning]       = useState({});
  const [filter, setFilter]             = useState('all');

  const handleAssignDelivery = async (orderId) => {
    const form = deliveryForm[orderId] || {};
    if (!form.name || !form.phone) { alert('Enter delivery boy name and phone'); return; }
    setAssigning(prev => ({ ...prev, [orderId]: true }));
    try {
      await api.patch(`/api/farmer/orders/${orderId}/assign-delivery`, {
        delivery_boy_name: form.name, delivery_boy_phone: form.phone
      });
      onUpdateStatus(orderId, 'out_for_delivery');
    } catch (err) {
      alert('Failed to assign delivery boy');
    } finally {
      setAssigning(prev => ({ ...prev, [orderId]: false }));
    }
  };


  const filteredOrders = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const [perPage, setPerPage] = useState(5);
  const pagination = usePagination(filteredOrders, perPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { pagination.reset(); }, [filter]);

  if (loading) return (
    <div className="py-16 flex justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4 w-full overflow-hidden">

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
         <h3 className="font-bold text-sm sm:text-base lg:text-lg px-1">
          Orders
          <span className="ml-2 text-secondary font-normal text-xs sm:text-sm">
            ({orders.length} total)
          </span>
        </h3>
        <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-none text-[9px] sm:text-[10px] md:text-[11px] font-bold -mx-1 px-1">
          {['all','confirmed','out_for_delivery','completed','cancelled'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex-shrink-0 whitespace-nowrap px-1.5 sm:px-2 py-1 rounded-full border
                          font-bold transition-all cursor-pointer
                          ${filter === f
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white border-outline-variant/20 text-secondary hover:border-primary/40'
                          }`}>
              {orders.filter(o => f === 'all' || o.status === f).length} {f.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm
                        border border-outline-variant/10">
          <span className="material-symbols-outlined text-4xl text-outline-variant">
            receipt_long
          </span>
          <p className="mt-2 font-semibold text-secondary">No orders yet</p>
          <p className="text-xs mt-1 text-outline">
            Orders will appear here once buyers purchase your animals
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {pagination.slice.map(o => (
            <div key={o.id}
              className="bg-white rounded-2xl shadow-sm border border-outline-variant/10
                         overflow-hidden hover:border-primary/20 transition-all w-full">

              {/* ── Card top row ── */}
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4 p-2 sm:p-3 md:p-4 cursor-pointer overflow-hidden"
                  onClick={() => setExpanded(expanded === o.id ? null : o.id)}>

                {/* Animal image */}
                {o.image_url
                  ? <img src={o.image_url} alt={o.animal_name}
                         className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg sm:rounded-xl object-cover flex-shrink-0" />
                  : <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg sm:rounded-xl bg-surface-container
                                    flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-lg sm:text-xl md:text-2xl text-outline-variant">
                        pets
                      </span>
                    </div>
                }

                {/* Animal + order info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <p className="font-bold text-xs sm:text-sm md:text-base on-surface truncate">{o.animal_name}</p>
                    {o.category && (
                      <span className="text-[8px] sm:text-[9px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full
                                       bg-primary/10 text-primary">
                        {o.category}
                      </span>
                    )}
                    {o.breed && (
                      <span className="text-[8px] sm:text-[9px] text-secondary">{o.breed}</span>
                    )}
                  </div>
                  <p className="text-[9px] sm:text-[10px] text-secondary mt-0.5 font-mono">
                    Order #{o.id} · {new Date(o.created_at).toLocaleDateString('en-IN')}
                  </p>
                </div>

                {/* Amount */}
                <div className="text-right flex-shrink-0 min-w-0">
                  <p className="font-black text-primary text-xs sm:text-sm md:text-base whitespace-nowrap">
                    ₹{Number(o.amount || 0).toLocaleString('en-IN')}
                  </p>
                  <span className={`text-[8px] sm:text-[9px] md:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full 
                                    block whitespace-nowrap ${statusStyle(o.status)}`}>
                    {o.status.replace(/_/g, ' ')}
                  </span>
                </div>

                <span className="p-1 text-secondary flex-shrink-0">
                  <span className="material-symbols-outlined text-[18px] sm:text-[20px]">
                    {expanded === o.id ? 'expand_less' : 'expand_more'}
                  </span>
                </span>
              </div>

              {/* ── Expanded details ── */}
              {expanded === o.id && (
                <div className="border-t border-outline-variant/10 p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3 md:space-y-4
                                bg-surface-container-low/40">

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">

                    {/* Buyer details */}
                    <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-outline-variant/10">
                      <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest
                                    text-secondary mb-2 sm:mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px]">person</span>
                        Buyer Details
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center
                                          justify-center text-primary font-black text-sm flex-shrink-0">
                            {(o.buyer_name || 'B')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-bold truncate">{o.buyer_name || '—'}</p>
                            <p className="text-[10px] text-secondary">Customer</p>
                          </div>
                        </div>
                        {o.buyer_phone && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm text-secondary">
                            <span className="material-symbols-outlined text-[16px] flex-shrink-0">call</span>
                            <a href={`tel:${o.buyer_phone}`}
                               className="hover:text-primary transition-colors font-medium break-all">
                              {o.buyer_phone}
                            </a>
                          </div>
                        )}
                        {o.buyer_email && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm text-secondary">
                            <span className="material-symbols-outlined text-[16px] flex-shrink-0">mail</span>
                            <span className="truncate">{o.buyer_email}</span>
                          </div>
                        )}
                        {(o.buyer_city || o.buyer_state) && (
                          <div className="flex items-start gap-2 text-xs sm:text-sm text-secondary">
                            <span className="material-symbols-outlined text-[16px] flex-shrink-0">location_on</span>
                            <span>
                              {[o.buyer_city, o.buyer_state].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delivery + note */}
                    <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-outline-variant/10">
                      <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest
                                    text-secondary mb-2 sm:mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px]">local_shipping</span>
                        Delivery Info
                      </p>
                      <div className="space-y-2">
                        {o.delivery_address ? (
                          <div className="flex items-start gap-2 text-xs sm:text-sm text-secondary">
                            <span className="material-symbols-outlined text-[16px] mt-0.5 flex-shrink-0">
                              home
                            </span>
                            <span className="break-words">{o.delivery_address}</span>
                          </div>
                        ) : (
                          <p className="text-xs sm:text-sm text-outline italic">
                            No delivery address provided
                          </p>
                        )}
                        {o.note && (
                          <div className="mt-2 pt-2 border-t border-outline-variant/10">
                            <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest
                                          text-secondary mb-1">Note from buyer</p>
                            <p className="text-xs sm:text-sm text-on-surface italic break-words">"{o.note}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions for pending orders */}
                  {o.status === 'pending' && onUpdateStatus && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <button
                        onClick={() => onUpdateStatus(o.id, 'confirmed')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 sm:py-2.5
                                   rounded-lg sm:rounded-xl bg-emerald-600 text-white font-bold text-xs sm:text-sm
                                   hover:bg-emerald-700 transition-all">
                        <span className="material-symbols-outlined text-[16px] sm:text-[18px]">check_circle</span>
                        Accept Order
                      </button>
                      <button
                        onClick={() => onUpdateStatus(o.id, 'cancelled')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 sm:py-2.5
                                   rounded-lg sm:rounded-xl bg-red-50 text-red-600 font-bold text-xs sm:text-sm
                                   border border-red-100 hover:bg-red-600 hover:text-white
                                   transition-all">
                        <span className="material-symbols-outlined text-[16px] sm:text-[18px]">cancel</span>
                        Reject Order
                      </button>
                    </div>
                  )}

                  {/* Mark completed for confirmed orders */}
                  {o.status === 'confirmed' && onUpdateStatus && (
                    <div className="space-y-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-secondary">
                        Assign Delivery Boy
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          placeholder="Delivery boy name"
                          value={deliveryForm[o.id]?.name || ''}
                          onChange={e => setDeliveryForm(prev => ({
                            ...prev, [o.id]: { ...prev[o.id], name: e.target.value }
                          }))}
                          className="flex-1 w-full border border-outline-variant/30 rounded-xl px-3 py-2 text-sm
                                    focus:outline-none focus:border-primary"
                        />
                        <input
                          placeholder="Phone number"
                          value={deliveryForm[o.id]?.phone || ''}
                          onChange={e => setDeliveryForm(prev => ({
                            ...prev, [o.id]: { ...prev[o.id], phone: e.target.value }
                          }))}
                          className="flex-1 w-full border border-outline-variant/30 rounded-xl px-3 py-2 text-sm
                                    focus:outline-none focus:border-primary"
                        />
                      </div>
                      <button
                        onClick={() => handleAssignDelivery(o.id)}
                        disabled={assigning[o.id]}
                        className="w-full flex items-center justify-center gap-2 py-2.5
                                  rounded-xl bg-primary text-white font-bold text-sm
                                  hover:opacity-90 transition-all disabled:opacity-50">
                        <span className="material-symbols-outlined text-[18px]">local_shipping</span>
                        {assigning[o.id] ? 'Assigning...' : 'Assign & Dispatch'}
                      </button>
                    </div>
                  )}

                {/* OTP verified → mark completed */}
                {o.status === 'out_for_delivery' && onUpdateStatus && (
                  <div className="space-y-3">
                    {/* Delivery boy info */}
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                                    bg-purple-50 border border-purple-100">
                      <span className="material-symbols-outlined text-[18px] text-purple-600">
                        delivery_dining
                      </span>
                      <span className="text-sm font-medium text-purple-700">
                        {o.delivery_boy_name} · {o.delivery_boy_phone}
                      </span>
                    </div>

                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3">
                      <span className="material-symbols-outlined text-amber-500 text-[22px]">info</span>
                      <div>
                        <p className="text-sm font-bold text-amber-800">Waiting for delivery confirmation</p>
                        <p className="text-[11px] text-amber-600">The delivery driver will confirm via OTP on delivery</p>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              )}

            </div>
          ))}
        </div>
      )}
      {filteredOrders.length > 0 && (
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1">
              <span className="text-xs text-secondary mr-1">Per page</span>
              {[5, 10, 20].map(n => (
                <button key={n} onClick={() => { setPerPage(n); pagination.reset(); }}
                  className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
                    ${perPage === n ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
                  {n}
                </button>
              ))}
            </div>
            <p className="text-xs text-secondary">
              {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, filteredOrders.length)} of {filteredOrders.length}
            </p>
          </div>
        )}
        <PaginationBar page={pagination.page} totalPages={pagination.totalPages} setPage={pagination.setPage} />
    </div>
  );
}